/* eslint-disable */
import Node from '../node.js';
import { PARAM_TYPE, PARAM_UNITS } from '../connector.js';
import Utils from '../utils.js';
import Utils3D from '../../utils/utils3D.js';
import Spline, { POINT_TYPE } from '../spline.js';

export default class NodeDistribute extends Node
{
	constructor(pParent)
	{
		super(pParent);

		this.addInput(PARAM_TYPE.GEOMETRY, 'object', null);
		this.addInput(PARAM_TYPE.GEOMETRY, 'target', null);

		this.addInput(PARAM_TYPE.FLOAT, 'override_width', 0.0, { units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'override_height', 0.0, { units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'override_depth', 0.0, { units: PARAM_UNITS.METERS });

		this.addInput(PARAM_TYPE.INTEGER, 'x count', 1, { minValue: 1, maxValue: 15 });
		this.addInput(PARAM_TYPE.FLOAT, 'x start padding', 0.0, { units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'x end padding', 0.0, { units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.BOOLEAN, 'x skip first', false);
		this.addInput(PARAM_TYPE.BOOLEAN, 'x skip last', false);
		this.addInput(PARAM_TYPE.BOOLEAN, 'x orient with target', false);
		this.addInputsToGroup('X Axis', ['x count', 'x start padding', 'x end padding', 'x skip first', 'x skip last', 'x orient with target']);

		this.addInput(PARAM_TYPE.INTEGER, 'y count', 1, { minValue: 1, maxValue: 15 });
		this.addInput(PARAM_TYPE.FLOAT, 'y start padding', 0.0, { units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'y end padding', 0.0, { units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.BOOLEAN, 'y skip first', false);
		this.addInput(PARAM_TYPE.BOOLEAN, 'y skip last', false);
		this.addInput(PARAM_TYPE.BOOLEAN, 'y orient with target', false);
		this.addInputsToGroup('Y Axis', ['y count', 'y start padding', 'y end padding', 'y skip first', 'y skip last', 'y orient with target']);

		this.addInput(PARAM_TYPE.INTEGER, 'z count', 1, { minValue: 1, maxValue: 15 });
		this.addInput(PARAM_TYPE.FLOAT, 'z start padding', 0.0, { units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'z end padding', 0.0, { units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.BOOLEAN, 'z skip first', false);
		this.addInput(PARAM_TYPE.BOOLEAN, 'z skip last', false);
		this.addInput(PARAM_TYPE.BOOLEAN, 'z orient with target', false);
		this.addInputsToGroup('Z Axis', ['z count', 'z start padding', 'z end padding', 'z skip first', 'z skip last', 'z orient with target']);

		this.addOutput(PARAM_TYPE.GEOMETRY, 'geo');
		this.addOutput(PARAM_TYPE.SPLINE, 'centers');

		this.lastComputerCenters = [];
	}

	getType()
	{
		return 'distribute';
	}

	computeSpacing(strAxis, inputValues, bboxTarget, bboxDistObjects)
	{
		let fSpaceAvailable = bboxTarget.maximum[strAxis] - bboxTarget.minimum[strAxis];
		fSpaceAvailable -= inputValues[strAxis + ' start padding'];
		fSpaceAvailable -= inputValues[strAxis + ' end padding'];

		// Special case 
		const iCount = inputValues[strAxis + ' count'];
		if(iCount <= 1)
			return fSpaceAvailable/2;

		let fAxisSize = bboxDistObjects.maximum[strAxis] - bboxDistObjects.minimum[strAxis];
		return (fSpaceAvailable - fAxisSize)/(iCount - 1);
	}

	generateSubclass(scene, inputValues, outputName, computedOutputResults)
	{
		if(outputName === 'centers')
		{
			let pFormattedPoints = this.lastComputerCenters.map((point) => { return {point: point, type: POINT_TYPE.LINEAR}; });
			return new Spline({ points: pFormattedPoints, isClosed: false });
		}

		this.lastComputerCenters = [];

		let pObjectsToDistribute = inputValues['object'];
		let pTargetObjects = inputValues['target'];
		// For now we only work on arrays of meshes
		if(!pObjectsToDistribute || !Array.isArray(pObjectsToDistribute))
			return null;

		pObjectsToDistribute = pObjectsToDistribute.filter((obj) => { return Utils.getIsMeshObject(obj); });

		// Get the bbox of the target node in its local space
		let currWorldMatrix;
		let vecTargetRotations;
		let vecAlignTargetCenterWorld;
		let vecAlignHalfSize;
		let bboxAlignTarget;
		if(pTargetObjects && pTargetObjects.length > 0)
		{
			pTargetObjects[0].computeWorldMatrix(true);
			currWorldMatrix = pTargetObjects[0].getWorldMatrix();

			bboxAlignTarget = Utils.getLocalSpaceBoundingBox(pTargetObjects, currWorldMatrix);
			let bboxAlignTargetWorld = pTargetObjects[0].getBoundingInfo().boundingBox;
			vecAlignTargetCenterWorld = bboxAlignTargetWorld.maximumWorld.add(bboxAlignTargetWorld.minimumWorld).scale(0.5);
			vecAlignHalfSize = bboxAlignTarget.maximum.subtract(bboxAlignTarget.minimum).scale(0.5);
			vecTargetRotations = pTargetObjects[0].rotation;
		}
		else
		{
			bboxAlignTarget = { minimum: BABYLON.Vector3.Zero(), maximum: BABYLON.Vector3.Zero() };
			currWorldMatrix = new BABYLON.Matrix.Identity();
			vecTargetRotations = BABYLON.Vector3.Zero();
			vecAlignHalfSize = BABYLON.Vector3.Zero();
			vecAlignTargetCenterWorld = BABYLON.Vector3.Zero();
		}

		if(inputValues['override_width'] > 0.0)
		{
			vecAlignHalfSize.x = inputValues['override_width']/2;
			bboxAlignTarget.minimum.x = -vecAlignHalfSize.x;
			bboxAlignTarget.maximum.x = vecAlignHalfSize.x;
		}
		if(inputValues['override_height'] > 0.0)
		{
			vecAlignHalfSize.y = inputValues['override_height']/2;
			bboxAlignTarget.minimum.y = -vecAlignHalfSize.y;
			bboxAlignTarget.maximum.y = vecAlignHalfSize.y;
		}

		if(inputValues['override_depth'] > 0.0)
		{
			vecAlignHalfSize.z = inputValues['override_depth']/2;
			bboxAlignTarget.minimum.z = -vecAlignHalfSize.z;
			bboxAlignTarget.maximum.z = vecAlignHalfSize.z;
		}

		let vecAxes = [];
		for(let rowIndex = 0; rowIndex < 3; rowIndex++)
		{
			let matrixRow = currWorldMatrix.getRow(rowIndex);
			vecAxes[rowIndex] = new BABYLON.Vector3(matrixRow.x, matrixRow.y, matrixRow.z);
			vecAxes[rowIndex].normalize();
		}

		// Now, compute spacings
		for(let pSrcMesh of pObjectsToDistribute)
		{
			if(inputValues['x orient with target'])
				pSrcMesh.rotation.x = vecTargetRotations.x;
			if(inputValues['y orient with target'])
				pSrcMesh.rotation.y = vecTargetRotations.y;
			if(inputValues['z orient with target'])
				pSrcMesh.rotation.z = vecTargetRotations.z;
		}
		const bboxDistObjects = Utils.getLocalSpaceBoundingBox(pObjectsToDistribute, currWorldMatrix);
		const fXStep = this.computeSpacing('x', inputValues, bboxAlignTarget, bboxDistObjects);
		const fYStep = this.computeSpacing('y', inputValues, bboxAlignTarget, bboxDistObjects);
		const fZStep = this.computeSpacing('z', inputValues, bboxAlignTarget, bboxDistObjects);

		// TODO: This won't work with aligning to target object orienation, but is necessary
		// to correctly distribute objects that aren't all centered at zero.
		let meshesBbox = Utils3D.getObjectsBBoxWithChildren(pObjectsToDistribute);
		let meshesCenter = meshesBbox.maximumWorld.add(meshesBbox.minimumWorld).scale(0.5);

		// Finally, clone
		let pNewMeshes = [];
		let iCounter = 0;

		const vecDistHalfSize = bboxDistObjects.maximum.subtract(bboxDistObjects.minimum).scale(0.5);
		for(let pSrcMesh of pObjectsToDistribute)
		{
			let bAddCenters = this.lastComputerCenters.length === 0;
			let fCurrXPosition = inputValues['x start padding'];
			if(inputValues['x count'] === 1)
				fCurrXPosition += fXStep;
			else
				fCurrXPosition += vecDistHalfSize.x;
			for(let iXIndex = 0; iXIndex < inputValues['x count']; iXIndex++)
			{
				let fCurrYPosition = inputValues['y start padding'];
				if(inputValues['y count'] === 1)
					fCurrYPosition += fYStep;
				else
					fCurrYPosition += vecDistHalfSize.y;
				for(let iYIndex = 0; iYIndex < inputValues['y count']; iYIndex++)
				{
					let fCurrZPosition = inputValues['z start padding'];
					if(inputValues['z count'] === 1)
						fCurrZPosition += fZStep;
					else
						fCurrZPosition += vecDistHalfSize.z;
					for(let iZIndex = 0; iZIndex < inputValues['z count']; iZIndex++)
					{
						if(inputValues['x skip first'] && iXIndex === 0
							|| inputValues['x skip last'] && iXIndex === inputValues['x count'] - 1)
							continue;

						if(inputValues['y skip first'] && iYIndex === 0
							|| inputValues['y skip last'] && iYIndex === inputValues['y count'] - 1)
							continue;

						if(inputValues['z skip first'] && iZIndex === 0
							|| inputValues['z skip last'] && iZIndex === inputValues['z count'] - 1)
							continue;

						let currNeshesBbox = Utils3D.getObjectsBBoxWithChildren([pSrcMesh]);
						let currMeshesCenter = currNeshesBbox.maximumWorld.add(currNeshesBbox.minimumWorld).scale(0.5);
						let vecCentersDiff = currMeshesCenter.subtract(meshesCenter);

						let pNewMesh = Utils3D.cloneMesh(pSrcMesh, this.getGeneratedMeshName() + "_" + iCounter);
						this.onMeshCreated(pNewMesh);
						iCounter++;

						// Convert the position from a local one to world space.
						let vecPosition = vecAxes[0].scale(fCurrXPosition - vecAlignHalfSize.x).add(vecAxes[1].scale(fCurrYPosition - vecAlignHalfSize.y)).add(vecAxes[2].scale(fCurrZPosition - vecAlignHalfSize.z));
						pNewMesh.position = vecPosition.add(vecAlignTargetCenterWorld).add(vecCentersDiff);

						if(bAddCenters)
							this.lastComputerCenters.push(pNewMesh.position.clone());

						pNewMeshes.push(pNewMesh);

						fCurrZPosition += fZStep;
					}

					fCurrYPosition += fYStep;
				}

				fCurrXPosition += fXStep;
			}
		}

		// Dispose of the originals
		for(let pSrcMesh of pObjectsToDistribute)
			pSrcMesh.dispose();

		return pNewMeshes;
	}
}